import { PAGE_TITLES } from 'Constants/page.constants'
import { useTitle } from 'Hooks/use-title'
import styles from './TimesheetForm.module.less'
import { Button, Card, DatePicker, Form, Input, InputNumber, Select, TimePicker } from 'antd/lib'
import { ArrowLeftOutlined, CheckCircleFilled, WarningOutlined } from '@ant-design/icons'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect } from 'react'
import { submitTimeTrackingData } from 'Services/projects.service'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import HeaderComp from 'Components/common/Header/Header'
import { HomeTabKeys, META_PROJECT_ID } from 'Constants/constants'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { AttributeDescription } from 'Components/tasks/Attributes/AttributeDescription'
import utc from 'dayjs/plugin/utc'
import { useMessage } from 'shared/hooks/use-message'

dayjs.extend(utc)

type FormValuesType = {
    platform: string
    date: Dayjs
    completedTasks: number
    time: Dayjs
    comments?: string
}

const PLATFORM_OPTIONS = [
    {
        label: 'Parimango',
        value: 'https://www.parimango.com',
    },
]

export const TimesheetForm = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [form] = Form.useForm<FormValuesType>()
    useTitle(PAGE_TITLES.TIME_TRACKING)
    const { userProfile } = useUserProfile()
    const { projectId } = useParams()
    const queryClient = useQueryClient()
    const [messageApi] = useMessage()

    useEffect(() => {
        const isUserAssociated = location.state?.timesheet_authorized === true

        if (!isUserAssociated) {
            navigate('/', {
                state: {
                    type: HomeTabKeys.Timesheet,
                },
            })
        }
    }, [location.state, navigate])

    const {
        mutate: submitForm,
        isPending: isSubmitting,
        isSuccess: isSubmitted,
    } = useMutation({
        mutationFn: async (values: FormValuesType) => {
            const date = dayjs.utc(values.date.format('YYYY-MM-DD')).toDate()
            const submissionData = {
                form_values: {
                    platform: values.platform,
                    completed_tasks: values.completedTasks,
                    time: values.time.format('HH:mm:ss'),
                    task_solved_date: date,
                    comments: values.comments,
                },
                platform: values.platform,
                task_solved_date: date,
                project_id: projectId!,
                user_id: userProfile.user.user_id,
            }
            return await submitTimeTrackingData(submissionData)
        },
        onSuccess: () => {
            messageApi.success('Time sheet submitted successfully!')
            form.resetFields()
            queryClient.invalidateQueries({
                queryKey: ['timeSheetEntries', META_PROJECT_ID, userProfile.user.user_id],
            })
        },
        onError: error => {
            console.error('Error submitting time tracking data:', error)
            messageApi.error(error ? (error as unknown as string) : 'Failed to submit time sheet')
        },
    })

    const onFinishFailed = () => {
        messageApi.error('Please fill in all the fields correctly.')
    }

    const goBackToTimesheet = useCallback(() => {
        navigate('/', {
            state: {
                type: HomeTabKeys.Timesheet,
            },
        })
    }, [navigate])

    const getLabelElement = (label: string, description: string, mandatory: boolean = true) => (
        <div className={styles['label-element']}>
            <span className={styles['label-text']}>{label}</span>
            {mandatory && <span className={styles['mandatory-text']}>*</span>}
            <AttributeDescription description={description} />
        </div>
    )

    return (
        <div className={styles['timesheet-form']}>
            <HeaderComp />
            <div className={styles['body']}>
                <div className={styles['instruction-tab']}>
                    <button onClick={goBackToTimesheet} className={styles['back-to-timesheet']}>
                        <ArrowLeftOutlined /> Back
                    </button>
                    <div className={styles['instruction-container']}>
                        <h1 className={styles['instruction-title']}>
                            Daily Task and Time Reporting Instructions
                        </h1>

                        <h2 className={styles['instruction-subtitle']}>Overview</h2>
                        <p className={styles['instruction-description']}>
                            Please use this form to report your daily progress, including completed
                            tasks and time spent. This helps us ensure accurate and fair
                            compensation for your valuable work.
                        </p>

                        <h2 className={styles['instruction-subtitle']}>Accountability Notice!</h2>
                        <ul>
                            <li>
                                Contributors are fully responsible for ensuring their time
                                submissions are accurate, complete, and compliant with these
                                guidelines.
                            </li>
                            <li>
                                Submissions that contain inaccurate, inflated, duplicated, or
                                overlapping hours may be rejected, corrected without notice, or
                                excluded from payment.
                            </li>
                            <li>
                                Repeated or intentional misuse of the reporting process may result
                                in restricted reporting access or removal from the project.
                            </li>
                        </ul>

                        <h2 className={styles['instruction-subtitle']}>
                            How to Complete Your Report
                        </h2>

                        <h3 className={styles['instruction-subtitle']}>1. Reporting Frequency</h3>
                        <ul>
                            <li>
                                Submit <strong>one report per day</strong>.
                            </li>
                            <li>
                                Your report must include all tasks completed and the total
                                aggregated time worked for that day.
                            </li>
                            <li>
                                Total reported time must{' '}
                                <strong>not exceed 10 hours per day.</strong>
                            </li>
                            <li>
                                Reports should be submitted at the end of the day, once all work is
                                completed.
                            </li>
                        </ul>

                        <h4 className={styles['instruction-subtitle']}>Corrections</h4>
                        <ul>
                            <li>
                                In rare cases, if you need to edit or replace a previously submitted
                                report, submit a new form using <strong>"Correction"</strong> in the
                                comment field.
                            </li>
                            <li>
                                A correction must include the <strong>full aggregated time</strong>{' '}
                                for the day.
                            </li>
                            <li>
                                The latest submission overrides all previous entries for that day.
                            </li>
                        </ul>

                        <h4 className={styles['instruction-subtitle']}>Example:</h4>
                        <ul>
                            <li>
                                You worked 2 hrs + 3 hrs + 4 hrs = <strong>9 hours total</strong> →
                                Submit as <strong>"New Entry"</strong>
                            </li>
                            <li>
                                Later, you make edits to the entry and add 30 minutes → Submit{' '}
                                <strong>9 hrs 30 mins</strong> with comment{' '}
                                <strong>"Correction"</strong>
                            </li>
                        </ul>

                        <h4 className={styles['instruction-subtitle']}>
                            <WarningOutlined className={styles['warning-icon']} /> Important:
                        </h4>
                        <ul>
                            <li>Corrections can be used to edit or replace previous entries.</li>
                            <li>
                                Corrections must always reflect the entire day's cumulative hours.
                            </li>
                            <li>Do not submit incremental or partial time updates.</li>
                            <li>
                                Any anomalies or incorrect submissions may{' '}
                                <strong>not be compensated</strong>.
                            </li>
                        </ul>

                        <h3 className={styles['instruction-subtitle']}>
                            2. Date & Time Entry Format
                        </h3>
                        <p className={styles['instruction-description']}>
                            <strong>Date:</strong> Enter the date you completed the tasks.
                            <br />
                            <strong>Time:</strong> Enter the effective hours, minutes, and seconds
                            you took to complete the tasks.
                        </p>

                        <h3 className={styles['instruction-subtitle']}>3. Platform selection</h3>
                        <ul>
                            <li>Select the correct platform used:</li>
                            <ul>
                                <li>Parimango</li>
                            </ul>
                        </ul>

                        <h3 className={styles['instruction-subtitle']}>4. Task Details</h3>
                        <ul>
                            <li>
                                Only use <strong>"New Entry"</strong> /OR{' '}
                                <strong>"Correction"</strong>
                            </li>
                            <li>
                                <strong>"New Entry"</strong> – Final aggregated entry for the day
                                (required)
                            </li>
                            <li>
                                <strong>"Correction"</strong> – Used only to replace a previously
                                submitted entry with the correct cumulative total
                            </li>
                            <li>
                                Your final submission must always reflect the total hours worked for
                                that day.
                            </li>
                        </ul>

                        <h2 className={styles['instruction-subtitle']}>Important Notes</h2>
                        <ul>
                            <li>Accuracy is essential for proper compensation tracking.</li>
                            <li>Double-check all entries before submitting.</li>
                            <li>
                                Reports with formatting or policy errors will need to be revised.
                            </li>
                            <li>
                                For changes beyond adding missed hours, please contact your
                                supervisor.
                            </li>
                        </ul>

                        <h2 className={styles['instruction-subtitle']}>Questions?</h2>
                        <p className={styles['instruction-description']}>
                            If you're unsure about any part of this process, please reach out for
                            clarification before submitting.
                        </p>
                        <ul>
                            <li>What happens if I submitted incorrect hours?</li>
                            <ul>
                                <li>
                                    Resubmit the timesheet using <strong>"Correction"</strong> and
                                    include the full cumulative hours for that day. The latest entry
                                    will replace the previous one.
                                </li>
                            </ul>
                            <li>Do I need to log breaks or only productive hours?</li>
                            <ul>
                                <li>Only log hours spent actively working on assigned tasks.</li>
                            </ul>
                            <li>How should I track partial tasks that are not yet completed?</li>
                            <ul>
                                <li>
                                    Record the hours worked so far and mark the task as "in
                                    progress." You can continue updating until the task is complete.
                                </li>
                            </ul>
                            <li>Should I round my hours (e.g., 2.75 → 3)?</li>
                            <ul>
                                <li>
                                    No, please record exact hours (to the nearest 0.25 or 15
                                    minutes, depending on project rules)
                                </li>
                            </ul>
                            <li>I have some other issues, what should I do?</li>
                            <ul>
                                <li>
                                    If the issue is still not resolved, reach out to the GenAI team
                                    by writing to{' '}
                                    <a href="mailto:community_support@telusinternational.com">
                                        community_support@telusinternational.com
                                    </a>{' '}
                                    include in the email subject: <strong>Project Sonic</strong>
                                </li>
                            </ul>
                        </ul>

                        <h2 className={styles['instruction-subtitle']}>
                            Thank you for your attention to detail in helping us track your
                            contributions accurately!
                        </h2>
                    </div>
                </div>
                <div className={styles['time-tracking-tab']}>
                    <Card className={styles['time-tracking-card']}>
                        {isSubmitted ? (
                            <div className={styles['success-container']}>
                                <CheckCircleFilled className={styles['success-icon']} />
                                <h3 className={styles['success-title']}>Success!</h3>
                                <p className={styles['success-message']}>
                                    Your time sheet has been submitted successfully.
                                </p>
                            </div>
                        ) : (
                            <Form
                                layout="vertical"
                                onFinish={submitForm}
                                onFinishFailed={onFinishFailed}
                                form={form}
                                validateTrigger={['onBlur', 'onSubmit']}
                            >
                                <Form.Item
                                    className={styles['form-item']}
                                    label={getLabelElement(
                                        'Platform',
                                        'Please select the correct platform where the tasks were completed.'
                                    )}
                                    name="platform"
                                    htmlFor=""
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select a platform!',
                                        },
                                    ]}
                                >
                                    <Select
                                        options={PLATFORM_OPTIONS}
                                        placeholder="Select a platform"
                                        allowClear
                                    />
                                </Form.Item>

                                <Form.Item
                                    className={styles['form-item']}
                                    label={getLabelElement(
                                        'Date',
                                        'Input the date you completed your tasks'
                                    )}
                                    name="date"
                                    htmlFor=""
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select a date!',
                                        },
                                    ]}
                                >
                                    <DatePicker
                                        placeholder="Select date"
                                        format="YYYY-MM-DD"
                                        disabledDate={current =>
                                            current &&
                                            (current > dayjs().endOf('day') ||
                                                current <
                                                    dayjs().subtract(3, 'days').startOf('day'))
                                        }
                                    />
                                </Form.Item>

                                <Form.Item
                                    className={styles['form-item']}
                                    label={getLabelElement(
                                        'Completed Tasks',
                                        'Input the number of completed tasks you did on the mentioned date.'
                                    )}
                                    name="completedTasks"
                                    htmlFor=""
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                'Please enter valid number of completed tasks!',
                                        },
                                        {
                                            type: 'number',
                                            min: 1,
                                            message: 'Number of tasks must be at least 1',
                                        },
                                        {
                                            type: 'number',
                                            max: 10000,
                                            message:
                                                'Number of tasks must not exceed 10,000 per day',
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        placeholder="Enter number of completed tasks"
                                        type="number"
                                        precision={0}
                                    />
                                </Form.Item>

                                <Form.Item
                                    className={styles['form-item']}
                                    label={getLabelElement(
                                        'Amount of Time',
                                        'Input the amount of hours and minutes that you have worked for. It should be in the format as HH:MM:SS'
                                    )}
                                    name="time"
                                    htmlFor=""
                                    validateTrigger={['onChange']}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select the amount of time spent!',
                                        },
                                        {
                                            validator: (_, value) => {
                                                if (!value) return Promise.resolve()
                                                const totalSeconds =
                                                    value.hour() * 3600 +
                                                    value.minute() * 60 +
                                                    value.second()
                                                return totalSeconds <= 10 * 3600
                                                    ? Promise.resolve()
                                                    : Promise.reject(
                                                          new Error(
                                                              'Time per day must not exceed 10:00:00'
                                                          )
                                                      )
                                            },
                                        },
                                    ]}
                                >
                                    <TimePicker
                                        placeholder="Select time spent"
                                        format="HH:mm:ss"
                                        showNow={false}
                                    />
                                </Form.Item>

                                <Form.Item
                                    className={styles['form-item']}
                                    label={getLabelElement(
                                        'Comments',
                                        'Input any additional comments you want to add to your time sheet.',
                                        false
                                    )}
                                    name="comments"
                                    htmlFor=""
                                >
                                    <Input.TextArea
                                        placeholder="Enter any additional comments"
                                        maxLength={1200}
                                    />
                                </Form.Item>

                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    className={styles['time-tracking-button']}
                                    size="middle"
                                    loading={isSubmitting}
                                >
                                    Submit
                                </Button>
                            </Form>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    )
}
